<div class="dash-shell">
  <!-- ═══════════════════════════════════════════════════════════════════════
       PAGE HEADER
       ═══════════════════════════════════════════════════════════════════════ -->
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page">Admin</span>
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">Operations overview</h1>
        <p class="page-head__subtitle">Live analytics across Kickstart Cash and Debtors Finance</p>
      </div>
      <div class="page-head__actions">
        <span class="dash-window-badge">Last {{ windowDays() }} days</span>
      </div>
    </div>
  </header>

  <!-- ═══════════════════════════════════════════════════════════════════════
       LOADING / ERROR STATES
       ═══════════════════════════════════════════════════════════════════════ -->
  @if (isLoading()) {
    <div class="dash-loading" aria-label="Loading operations overview">
      <div class="dash-kpi-grid dash-kpi-grid--3">
        @for (r of [1, 2, 3, 4, 5, 6]; track r) {
          <div class="dash-skeleton" style="min-height: 72px"></div>
        }
      </div>
      <div class="sg-panel dash-panel">
        <div class="sg-panel__header">
          <span class="sg-skeleton sg-skeleton--text" style="width: 200px"></span>
        </div>
        <div class="dash-table-wrap">
          <table class="sg-table sg-table--skeleton">
            <thead>
              <tr>
                <th>Product</th>
                <th>Reference</th>
                <th>Business</th>
                <th class="sg-cell--num">Amount</th>
                <th>Status</th>
                <th>AI</th>
                <th>Handler</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              @for (r of [1, 2, 3, 4, 5]; track r) {
                <tr>
                  <td><span class="sg-skeleton sg-skeleton--chip"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w70"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w80"></span></td>
                  <td class="sg-cell--num">
                    <span
                      class="sg-skeleton sg-skeleton--text"
                      style="width: 60%; margin-left: auto"
                    ></span>
                  </td>
                  <td><span class="sg-skeleton sg-skeleton--chip"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--chip"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w50"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span></td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  } @else if (loadError()) {
    <div class="dash-error sg-empty sg-empty--bordered">
      <app-icon name="x-circle" [size]="40" />
      <p class="sg-empty__title">Couldn't load the overview</p>
      <p class="sg-empty__desc">
        Something went wrong on our side.
      </p>
      <button mat-stroked-button (click)="retry()">
        <app-icon name="refresh" [size]="18" /> Try again
      </button>
    </div>
  } @else if (analytics(); as data) {
    <!-- ─── 1. METRIC CARDS — platform health (application analytics live in Ops) ─ -->
    <section class="dash-section" aria-label="Key metrics">
      <div class="dash-kpi-grid dash-kpi-grid--3">
        <a
          class="dash-kpi dash-kpi--compact dash-kpi--link dash-enter"
          [style.--dash-index]="0"
          routerLink="/admin/ai-evaluations"
          aria-label="View all AI evaluations"
        >
          <div class="dash-kpi__top">
            <span class="dash-kpi__icon"><app-icon name="sparkles" [size]="18" /></span>
            <app-icon name="arrow-right" [size]="14" class="dash-kpi__go" />
          </div>
          <div>
            <p class="dash-kpi__value">{{ data.ai.total_evaluations | dash }}</p>
            <p class="dash-kpi__label">AI evaluations</p>
          </div>
        </a>

        <a
          class="dash-kpi dash-kpi--compact dash-kpi--link dash-enter"
          [style.--dash-index]="1"
          routerLink="/admin/ai-evaluations"
          aria-label="View AI spend breakdown"
        >
          <div class="dash-kpi__top">
            <span class="dash-kpi__icon"><app-icon name="tag" [size]="18" /></span>
            <app-icon name="arrow-right" [size]="14" class="dash-kpi__go" />
          </div>
          <div>
            <p class="dash-kpi__value">{{ data.ai.cost_zar_total | zar }}</p>
            <p class="dash-kpi__label">AI spend</p>
          </div>
        </a>

        <button
          type="button"
          class="dash-kpi dash-kpi--compact dash-kpi--link dash-enter"
          [style.--dash-index]="2"
          (click)="scrollTo('recent-emails')"
          aria-label="Jump to recent emails sent"
        >
          <div class="dash-kpi__top">
            <span class="dash-kpi__icon"><app-icon name="inbox" [size]="18" /></span>
            <app-icon name="arrow-right" [size]="14" class="dash-kpi__go" />
          </div>
          <div>
            <p class="dash-kpi__value">{{ data.emails.sent | dash }}</p>
            <p class="dash-kpi__label">Emails sent</p>
          </div>
        </button>
      </div>
      <p class="dash-hint">
        Application volumes and decisions live in the
        <a routerLink="/ops/dashboard">Ops review queue</a>.
      </p>
    </section>

    <!-- ─── 2. TWO-UP: Workload + Emails chart ────────────────────────────── -->
    <section class="dash-section dash-two-up" aria-label="Workload and email notifications">
      <!-- Workload by reviewer -->
      <div class="sg-panel dash-panel">
        <div class="sg-panel__header">
          <div>
            <p class="sg-panel__title">Workload by reviewer</p>
            <p class="sg-panel__subtitle">Applications decided per ops user</p>
          </div>
          <app-icon name="users" [size]="18" class="dash-panel-icon" />
        </div>
        <div class="sg-panel__body dash-handlers-body">
          @if (!data.handlers.length) {
            <div class="dash-chart-empty">
              <app-icon name="users" [size]="32" />
              <p>No reviewer activity yet</p>
            </div>
          } @else {
            <div class="dash-handlers">
              @for (h of data.handlers; track h.name) {
                <div class="dash-handler-row">
                  <span class="dash-handler-row__name">{{ h.name }}</span>
                  <div class="dash-handler-row__bar-wrap" aria-hidden="true">
                    <div
                      class="dash-handler-row__bar"
                      [style.width]="handlerBarWidth(h.decided)"
                    ></div>
                  </div>
                  <span class="dash-handler-row__count">{{ h.decided }}</span>
                </div>
              }
            </div>
          }
        </div>
      </div>

      <!-- Emails sent chart -->
      <div class="sg-panel dash-panel">
        <div class="sg-panel__header">
          <div>
            <p class="sg-panel__title">Notifications sent</p>
            <p class="sg-panel__subtitle">
              {{ data.emails.sent }} sent · {{ data.emails.failed }} failed
            </p>
          </div>
          <app-icon name="tag" [size]="18" class="dash-panel-icon" />
        </div>
        <div class="sg-panel__body dash-chart-body">
          @if (!data.emails.by_day.days.length) {
            <div class="dash-chart-empty">
              <app-icon name="tag" [size]="32" />
              <p>No email data yet</p>
            </div>
          } @else {
            <app-chart [option]="emailsChartOption()" height="220px" />
          }
        </div>
      </div>
    </section>

    <!-- ─── RECENT EMAILS feed ─────────────────────────────────────────────── -->
    <section class="dash-section" aria-label="Recent emails" id="recent-emails">
      <div class="sg-panel dash-panel">
        <div class="sg-panel__header">
          <div>
            <p class="sg-panel__title">Recent emails</p>
            <p class="sg-panel__subtitle">Latest platform notifications sent</p>
          </div>
        </div>
        <div class="dash-table-wrap">
          @if (!recentEmails().length) {
            <div class="dash-chart-empty dash-chart-empty--pad">
              <app-icon name="tag" [size]="32" />
              <p>No emails sent yet</p>
            </div>
          } @else {
            <table class="sg-table" mat-table [dataSource]="recentEmails()">
              <ng-container matColumnDef="category_display">
                <th mat-header-cell *matHeaderCellDef>Category</th>
                <td mat-cell *matCellDef="let row">
                  <span class="dash-email-cat">{{ row.category_display }}</span>
                </td>
              </ng-container>

              <ng-container matColumnDef="subject">
                <th mat-header-cell *matHeaderCellDef>Subject</th>
                <td mat-cell *matCellDef="let row" class="dash-email-subject">{{ row.subject }}</td>
              </ng-container>

              <ng-container matColumnDef="to_email">
                <th mat-header-cell *matHeaderCellDef>Recipient</th>
                <td mat-cell *matCellDef="let row" class="dash-muted">{{ row.to_email }}</td>
              </ng-container>

              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let row">
                  <app-status-chip [status]="row.status" />
                </td>
              </ng-container>

              <ng-container matColumnDef="created_at">
                <th mat-header-cell *matHeaderCellDef>Sent</th>
                <td mat-cell *matCellDef="let row" class="dash-muted">
                  {{ row.created_at | date: 'medium' }}
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="emailColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: emailColumns" class="sg-table__row"></tr>
            </table>
          }
        </div>
      </div>
    </section>

    <!-- ─── 7. TOOLS — kept from original ────────────────────────────────── -->
    <section class="dash-section" aria-label="Admin tools">
      <h2 class="dash-section-heading">Tools</h2>
      <div class="dash-tools-grid">
        <a class="dash-tool-card" routerLink="/admin/users">
          <div class="dash-tool-card__icon">
            <app-icon name="users" [size]="22" />
          </div>
          <div>
            <p class="dash-tool-card__title">Users</p>
            <p class="dash-tool-card__desc">
              Manage staff accounts, roles, and authority tiers. Invite new team members.
            </p>
          </div>
          <app-icon name="arrow-right" [size]="16" class="dash-tool-card__arrow" />
        </a>

        <a class="dash-tool-card" routerLink="/admin/agencies">
          <div class="dash-tool-card__icon">
            <app-icon name="briefcase" [size]="22" />
          </div>
          <div>
            <p class="dash-tool-card__title">Agencies</p>
            <p class="dash-tool-card__desc">
              Set up broker agencies and their founding admin. Multiple agents share one client
              portfolio.
            </p>
          </div>
          <app-icon name="arrow-right" [size]="16" class="dash-tool-card__arrow" />
        </a>

        <a class="dash-tool-card" routerLink="/admin/email-approvals">
          <div class="dash-tool-card__icon">
            <app-icon name="mail" [size]="22" />
          </div>
          <div>
            <p class="dash-tool-card__title">Email approvals</p>
            <p class="dash-tool-card__desc">
              @if (analytics()?.emails?.waiting_approval; as waiting) {
                {{ waiting }} waiting for approval.
              }
              Send or delete email to anyone who is not staff before it goes out.
            </p>
          </div>
          <app-icon name="arrow-right" [size]="16" class="dash-tool-card__arrow" />
        </a>

        <a class="dash-tool-card" routerLink="/admin/emails">
          <div class="dash-tool-card__icon">
            <app-icon name="inbox" [size]="22" />
          </div>
          <div>
            <p class="dash-tool-card__title">Recent emails</p>
            <p class="dash-tool-card__desc">
              Browse the full log of platform notifications sent, filterable by category and status.
            </p>
          </div>
          <app-icon name="arrow-right" [size]="16" class="dash-tool-card__arrow" />
        </a>

        <a class="dash-tool-card" routerLink="/admin/ai-notes">
          <div class="dash-tool-card__icon">
            <app-icon name="sparkles" [size]="22" />
          </div>
          <div>
            <p class="dash-tool-card__title">Notes for AI</p>
            <p class="dash-tool-card__desc">
              Review staff notes on AI responses, then generate and accept the summaries the AI
              uses.
            </p>
          </div>
          <app-icon name="arrow-right" [size]="16" class="dash-tool-card__arrow" />
        </a>

        <a class="dash-tool-card" routerLink="/admin/feedback">
          <div class="dash-tool-card__icon">
            <app-icon name="message-square" [size]="22" />
          </div>
          <div>
            <p class="dash-tool-card__title">Feedback</p>
            <p class="dash-tool-card__desc">
              Triage the suggestions and bugs users submitted from the in-app Report button.
            </p>
          </div>
          <app-icon name="arrow-right" [size]="16" class="dash-tool-card__arrow" />
        </a>

        <a class="dash-tool-card" routerLink="/admin/system-status">
          <div class="dash-tool-card__icon">
            <app-icon name="shield" [size]="22" />
          </div>
          <div>
            <p class="dash-tool-card__title">System status</p>
            <p class="dash-tool-card__desc">
              Run live health checks on platform dependencies (database, S3 storage).
            </p>
          </div>
          <app-icon name="arrow-right" [size]="16" class="dash-tool-card__arrow" />
        </a>

        <a class="dash-tool-card" routerLink="/admin/user-journey">
          <div class="dash-tool-card__icon">
            <app-icon name="users" [size]="22" />
          </div>
          <div>
            <p class="dash-tool-card__title">User journey</p>
            <p class="dash-tool-card__desc">
              How an application moves through Flow: whose move it is, what is asked for, and how.
            </p>
          </div>
          <app-icon name="arrow-right" [size]="16" class="dash-tool-card__arrow" />
        </a>

        <a class="dash-tool-card" routerLink="/admin/compliance">
          <div class="dash-tool-card__icon">
            <app-icon name="alert-triangle" [size]="22" />
          </div>
          <div>
            <p class="dash-tool-card__title">Compliance &amp; security</p>
            <p class="dash-tool-card__desc">
              Outstanding POPIA and security items from the latest security review.
            </p>
          </div>
          <app-icon name="arrow-right" [size]="16" class="dash-tool-card__arrow" />
        </a>

        <a class="dash-tool-card" routerLink="/ops/dashboard">
          <div class="dash-tool-card__icon">
            <app-icon name="briefcase" [size]="22" />
          </div>
          <div>
            <p class="dash-tool-card__title">Ops review</p>
            <p class="dash-tool-card__desc">Review KS and DF applications in the ops queue.</p>
          </div>
          <app-icon name="arrow-right" [size]="16" class="dash-tool-card__arrow" />
        </a>
      </div>
    </section>
  }
  <!-- end @else if analytics -->
</div>
